import React, { useEffect, useState } from 'react'

import {
    useTheme,
    Modal,
    Paper,
    Slide,
    Grid,
    Tooltip,
    IconButton,
    Tabs,
    Tab,
    CircularProgress,
} from '@material-ui/core'

import CloseIcon from '@material-ui/icons/Close'

import { Container } from '../../../../components'

import {
    BaseWorkorder,
    ezScheduleDetails,
    getWorksheetTotals,
    PAYROLL_ACCESS,
    Schedule,
    Service,
} from '../../../../models'

import { ServiceTable } from '../../../../components/BudgetModal/Components/ServiceTable'
import { SummaryTable } from '../../../../components/BudgetModal/Components/SummaryTable'
import { BudgetPlot } from '../../../../components/BudgetModal/Components/BudgetPlot'
import { LineItemTable } from '../../../../components/BudgetModal/Components/LineItemTable'
import { VendorTable } from '../../../../components/BudgetModal/Components/VendorTable'

import { useTimezone } from '../../../../contexts/timezone/timezone'
import {
    AddLineItemParams,
    AddTemplateServiceParams,
    AddTemplateVendorParams,
    UpdateTemplateServiceParams,
    DeleteTemplateServiceParams,
    DeleteTemplateVendorParams,
    UpdateTemplateVendorParams,
    DeleteLineItemParams,
} from '../../../../hooks/useEzSchedule/types'

import { PayrollTracker } from '../../../../components/BudgetModal/Components/PayrollTracker'
import { useUser } from '../../../../hooks'
import { hasPermission } from '../../../../models/Users/services'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface Props {
    open: boolean
    onClose: () => void
    workorders: BaseWorkorder[]
    serviceList: Service[]
    scheduleDetail: Schedule | undefined
    portfolio?: boolean
    ezSchedule: ezScheduleDetails
    ezScheduleLoading: boolean
    addTemplateService: (params: AddTemplateServiceParams) => void
    addLineItem: (params: AddLineItemParams) => void
    deleteLineItem: (params: DeleteLineItemParams) => void
    addTemplateVendor: (params: AddTemplateVendorParams) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    updateTemplateVendor: (params: UpdateTemplateVendorParams) => void
    deleteTemplateVendor: (params: DeleteTemplateVendorParams) => void
}

export const BudgetModal = (props: Props) => {
    const {
        open,
        onClose,
        workorders,
        serviceList,
        scheduleDetail,
        portfolio,
        ezSchedule,
        ezScheduleLoading,
        addTemplateService,
        addLineItem,
        deleteLineItem,
        addTemplateVendor,
        updateTemplateService,
        deleteTemplateService,
        updateTemplateVendor,
        deleteTemplateVendor,
    } = props
    if (!scheduleDetail || !ezSchedule) {
        return (
            <Modal
                open={open}
                onClose={onClose}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
            </Modal>
        )
    }

    const timezoneState = useTimezone()

    const theme = useTheme()
    const { rootUser, workspaceUser } = useUser()
    const userForPayrollPermission =
        portfolio &&
        rootUser?.workspaces &&
        scheduleDetail?.apartment?.id != null
            ? rootUser.workspaces.find(
                  (ws) =>
                      ws.active_workspace?.id === scheduleDetail.apartment?.id,
              )
            : workspaceUser
    const showPayrollTab = hasPermission(
        userForPayrollPermission,
        PAYROLL_ACCESS,
    )

    const [serviceTotals, setServiceTotals] = useState<Totals>()
    const [lineItemTotals, setLineItemTotals] = useState<LineItemTotals>()
    const [payrollTotal, setPayrollTotal] = useState<number | undefined>(
        undefined,
    )
    const [selectedTab, setSelectedTab] = useState(0)
    const [budgetSubTab, setBudgetSubTab] = useState(0)

    const handleClose = () => {
        setSelectedTab(0)
        onClose()
    }

    const payrollOpts = portfolio ? { portfolio: true as const } : undefined
    useEffect(() => {
        if (!open || !scheduleDetail?.payroll_worksheet_id) return
        getWorksheetTotals(scheduleDetail.payroll_worksheet_id, payrollOpts)
            .then((r) => setPayrollTotal(Number(r.data.actual_total_pay)))
            .catch(() => {})
    }, [
        open,
        scheduleDetail?.id,
        scheduleDetail?.payroll_worksheet_id,
        portfolio,
    ])

    const handleTabChange = (
        event: React.ChangeEvent<{}>,
        newValue: number,
    ) => {
        setSelectedTab(newValue)
    }

    const handleBudgetSubTabChange = (
        event: React.ChangeEvent<{}>,
        newValue: number,
    ) => {
        setBudgetSubTab(newValue)
    }

    const HEADER_HEIGHT = 70

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        overflowY: 'scroll',
                        flexDirection: 'column',
                        height: '95%',
                        width: '90%',
                        padding: theme.spacing(2),
                        paddingTop: 0,
                    }}
                >
                    <Container style={{ flexDirection: 'column', flex: 1 }}>
                        {/* Header with top-level tabs */}
                        <Container
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexShrink: 0,
                                minHeight: HEADER_HEIGHT,
                                alignItems: 'center',
                            }}
                        >
                            <Tabs
                                value={selectedTab}
                                onChange={handleTabChange}
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                <Tab label="Budget" />
                                {showPayrollTab && (
                                    <Tab label="Payroll Tracker" />
                                )}
                            </Tabs>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    fontWeight: 700,
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {ezSchedule?.name ?? ''}
                            </span>
                            <div
                                style={{
                                    justifyContent: 'flex-end',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <Tooltip title="Close">
                                    <IconButton onClick={handleClose}>
                                        <CloseIcon fontSize="large" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </Container>

                        {/* Budget section - SummaryTable, Services/Vendors/Line Items, BudgetPlot */}
                        {selectedTab === 0 && (
                            <Grid container style={{ flex: 1, minHeight: 0 }}>
                                <Grid
                                    container
                                    xs={10}
                                    sm={10}
                                    md={8}
                                    lg={6}
                                    xl={5}
                                >
                                    <Grid item xs={12}>
                                        <SummaryTable
                                            theme={theme}
                                            ezSchedule={ezSchedule}
                                            workorders={workorders}
                                            totals={serviceTotals}
                                            lineItemTotals={lineItemTotals}
                                            scheduleDetail={scheduleDetail}
                                            portfolio
                                            payrollTotal={payrollTotal}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <div
                                            style={{
                                                borderBottom: '1px solid black',
                                                margin: '10px 0',
                                            }}
                                        ></div>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Tabs
                                            value={budgetSubTab}
                                            onChange={handleBudgetSubTabChange}
                                            indicatorColor="primary"
                                            textColor="primary"
                                            variant="fullWidth"
                                        >
                                            <Tab label="Services" />
                                            <Tab label="Vendors" />
                                            <Tab label="Line Items" />
                                        </Tabs>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        style={{
                                            minHeight: 500,
                                        }}
                                    >
                                        {budgetSubTab === 0 && (
                                            <ServiceTable
                                                theme={theme}
                                                addTemplateService={
                                                    addTemplateService
                                                }
                                                updateTemplateService={
                                                    updateTemplateService
                                                }
                                                deleteTemplateService={
                                                    deleteTemplateService
                                                }
                                                ezSchedule={ezSchedule}
                                                serviceList={serviceList}
                                                workorders={workorders}
                                                portfolio={portfolio}
                                            />
                                        )}
                                        {budgetSubTab === 1 && (
                                            <VendorTable
                                                theme={theme}
                                                ezSchedule={ezSchedule}
                                                templateVendors={
                                                    ezSchedule.template_vendors ||
                                                    []
                                                }
                                                workorders={workorders}
                                                addTemplateVendor={
                                                    addTemplateVendor
                                                }
                                                updateTemplateVendor={
                                                    updateTemplateVendor
                                                }
                                                deleteTemplateVendor={
                                                    deleteTemplateVendor
                                                }
                                            />
                                        )}
                                        {budgetSubTab === 2 && (
                                            <LineItemTable
                                                theme={theme}
                                                ezSchedule={ezSchedule}
                                                addLineItem={addLineItem}
                                                totals={lineItemTotals}
                                                scheduleDetail={scheduleDetail}
                                                deleteLineItem={deleteLineItem}
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    xs={2}
                                    sm={2}
                                    md={4}
                                    lg={6}
                                    xl={7}
                                >
                                    <BudgetPlot
                                        theme={theme}
                                        ezSchedule={ezSchedule}
                                        workorders={workorders}
                                        scheduleDetail={scheduleDetail}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {/* Payroll Tracker - full modal (only when user has Payroll access) */}
                        {showPayrollTab && selectedTab === 1 && (
                            <div
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <PayrollTracker
                                    scheduleDetail={scheduleDetail}
                                    onPayrollTotalChange={setPayrollTotal}
                                    portfolio={portfolio}
                                />
                            </div>
                        )}
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}

export interface Totals {
    budget: number
    variance: number
    forecast: number
    invoice: number
}

export interface LineItemTotals {
    cost: number
}
